// app/root.tsx
import { readPublishedSnapshot } from "@/i18n/publishedSnapshot.server";
import path from "node:path";
import { useEffect, useState, Suspense } from "react";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  isRouteErrorResponse,
  useRouteError,
} from "react-router";
import { QueryClientProvider } from "@tanstack/react-query";
import { I18nextProvider } from "react-i18next";
import i18next from "i18next";

import type { Route } from "./+types/root";
import { CookieConsent } from "@/components/CookieConsent";
import { ErrorBoundary as AppErrorBoundary } from "@/components/ErrorBoundary";
import { PreviewGate } from "@/components/PreviewGate";
import { RouteLoading } from "@/components/RouteLoading";
import { ScrollToTop } from "@/components/ScrollToTop";
import { StripeProvider, NetworkStatusProvider, CookieConsentProvider } from "@/contexts";
import { usePageTracking } from "@/hooks/usePageTracking";
import { useSession } from "@/hooks/useSession";
import { useSessionValidation } from "@/hooks/useSessionValidation";
import { useTokenRefresh } from "@/hooks/useTokenRefresh";
import { LocaleProvider } from "@/i18n/LocaleProvider";
import { LegacyRedirectBoundary } from "@/legacyRedirects/LegacyRedirectBoundary";
import { queryClient, createQueryClient } from "@/lib/queryClient";
import { env, validateEnv } from "@/lib/env";
import { initializeAnalytics } from "@/lib/telemetry";
import { initErrorTracking } from "@/lib/telemetry/errorTracking";
import { initEventTracking } from "@/lib/telemetry/eventTracker";
import { initOtel, recordWebVitalToOtel } from "@/lib/telemetry/otel";
import { reportWebVitals } from "@/lib/webVitals";
import "./index.css";

// Module-top-level (formerly in main.tsx) — safe under prerender because
// validateEnv reads import.meta.env, not window/localStorage.
validateEnv();

/** Use the published snapshot for both static HTML and client hydration. */
export function loader() {
  return readPublishedSnapshot(path.join(process.cwd(), "public"));
}

/**
 * Root `meta` export — fallback when no descendant route matches (or before
 * a route's own `meta` runs).
 *
 * Note on JSON-LD: RR v7 routes' `meta` exports REPLACE the parent's array
 * rather than merging, so the Organization + WebSite schemas can't live here
 * if we expect every page to ship them. They're emitted by `buildSeoMeta`
 * inside each route's meta instead — see `app/components/seo/meta.ts`.
 */
export function meta(_args: Route.MetaArgs) {
  return [
    { title: "Stork Rentals - Premium Baby Equipment" },
  ];
}

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
        <link rel="icon" href="/favicon.ico" />
        <link rel="apple-touch-icon" href="/apple-touch-icon.png" />
        <link rel="manifest" href="/site.webmanifest" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        {/* Brand typography — lost when index.html was deleted in Phase 3.
            DM Serif Display = h1/h2 display headings ("Quality Baby Gear");
            Nunito = body. Without these, display headings fall back to the
            sans-serif stack at default size/weight — the visible regression
            on staging Coming Soon page (CSS reduced from 104KB → 8KB also
            broke the rest; see tailwind.config.js content glob fix). */}
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=DM+Serif+Display&display=optional"
        />
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700&display=swap"
        />
        <link rel="dns-prefetch" href="https://js.stripe.com" />
        <meta name="theme-color" content="#0d9488" />
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  // A prerendered route must not inherit query data from an earlier route.
  const [renderQueryClient] = useState(() => import.meta.env.SSR ? createQueryClient() : queryClient);
  useEffect(() => {
    initializeAnalytics();
    initErrorTracking();
    initEventTracking();
    initOtel();

    reportWebVitals((metric) => {
      if (env.IS_PROD && window.gtag) {
        window.gtag("event", metric.name, {
          value: Math.round(metric.value),
          metric_id: metric.id,
          metric_rating: metric.rating,
        });
      }
      recordWebVitalToOtel(metric);
    });
  }, []);

  useTokenRefresh();
  usePageTracking();

  return (
    <I18nextProvider i18n={i18next}>
      <QueryClientProvider client={renderQueryClient}>
        <SessionValidationGate />
        <GuestSessionGate />
        <NetworkStatusProvider>
          <PreviewGate>
            <StripeProvider>
              <AppErrorBoundary>
                <LegacyRedirectBoundary>
                  <CookieConsentProvider>
                    <div className="min-h-screen bg-background font-sans antialiased">
                      <Suspense fallback={<RouteLoading />}>
                        <LocaleProvider>
                          <ScrollToTop />
                          <Outlet />
                        </LocaleProvider>
                      </Suspense>
                      <CookieConsent />
                    </div>
                  </CookieConsentProvider>
                </LegacyRedirectBoundary>
              </AppErrorBoundary>
            </StripeProvider>
          </PreviewGate>
        </NetworkStatusProvider>
      </QueryClientProvider>
    </I18nextProvider>
  );
}

/**
 * Side-effect-only child of QueryClientProvider that runs the boot-time
 * session validity probe. Lives inside the provider so `useQueryClient()`
 * resolves successfully — the prior call site in <App> was outside the
 * provider and threw "No QueryClient set" during prerender.
 */
function SessionValidationGate() {
  useSessionValidation();
  return null;
}

/**
 * Side-effect-only child that mints the server-side guest session cookie
 * (stork_sid) on first mount via POST /customer/session/init. Without this,
 * the durable session is never created and guest checkout's create-intent
 * fails with "Session ID or User ID is required" — the HARD-011 client wiring
 * that was previously missing.
 */
function GuestSessionGate() {
  useSession();
  return null;
}

export function ErrorBoundary() {
  const error = useRouteError();

  if (isRouteErrorResponse(error)) {
    return (
      <html lang="en">
        <head>
          <title>{`${error.status} ${error.statusText}`}</title>
          <Meta />
          <Links />
        </head>
        <body>
          <main className="min-h-screen flex items-center justify-center p-8">
            <div className="text-center">
              <h1 className="text-4xl font-bold">{error.status} {error.statusText}</h1>
              <p className="mt-4 text-muted-foreground">{error.data}</p>
            </div>
          </main>
          <Scripts />
        </body>
      </html>
    );
  }

  return (
    <html lang="en">
      <head>
        <title>Unexpected error</title>
        <Meta />
        <Links />
      </head>
      <body>
        <main className="min-h-screen flex items-center justify-center p-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold">Something went wrong</h1>
            <p className="mt-4 text-muted-foreground">
              {error instanceof Error ? error.message : "An unknown error occurred."}
            </p>
          </div>
        </main>
        <Scripts />
      </body>
    </html>
  );
}
